import type { FunctionComponent } from "react"

import dynamic from "next/dynamic"
import { useParams } from "next/navigation"

import { Heading } from "@jobteaser/spark/components/Heading"
import { Layout, LAYOUT_COLOR_BLOCK } from "@jobteaser/ui-kit/components/Layout/Layout"

import { RECRUITMENT_PATTERN_ID } from "@/features/companyDetail/components/shared/RecruitmentPatternInformation/RecruitmentPatternInformation"
import { useCompanyAnalyticsData } from "@/features/companyDetail/components/shared/RecruitmentPatternInformation/useCompanyAnalyticsData"
import { useCompanyInsightsFetch } from "@/features/companyDetail/hooks/useCompanyInsightsFetch"
import { getQuestions } from "@/features/companyDetail/utils/getQuestions"
import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { COMPANY_PAGE_TYPE } from "@/modules/companies/api/endpoints/company"
import { useCompanyFetch } from "@/modules/companies/hooks/useCompanyFetch"
import { COMPANY_BASENAME, COMPANY_JOB_OFFERS } from "@/modules/companies/utils/routing"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { Link } from "@/modules/routing/components/Link"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"

import styles from "./WorkThere.module.css"

const RecruitmentPatternBannerDynamic = dynamic(() =>
  import("@/features/companyDetail/components/shared/RecruitmentPatternBanner/RecruitmentPatternBanner").then(
    mod => mod.RecruitmentPatternBanner
  )
)

const InsightsDynamic = dynamic(
  () => import("@/features/companyDetail/components/Index/Insights/Insights").then(mod => mod.Insights),
  {
    ssr: false,
  }
)

const FaqDynamic = dynamic(() => import("@/features/companyDetail/components/Index/Faq/Faq").then(mod => mod.Faq), {
  ssr: false,
})

const CertificationDynamic = dynamic(
  () => import("@/features/companyDetail/components/Index/Certification/Certification").then(mod => mod.Certification),
  {
    ssr: false,
  }
)

export const WorkThere: FunctionComponent = () => {
  const { t } = useTranslation(["companies_client"])
  const locale = useLocale()
  const { companySlug } = useParams()
  const { dataCurrentUser } = useCurrentUserFetch()
  const { dataCompany } = useCompanyFetch()
  const { dataCareerCenter } = useCareerCenterFetch()
  const { dataCompanyInsights } = useCompanyInsightsFetch(dataCompany?.id || "")
  const { hasEnoughData } = useCompanyAnalyticsData()

  if (!dataCompany) return null

  const {
    id,
    page: { chooseMyCompany, faq, glassdoorProfileUrl, greatPlaceToWorkPermalink, greatPlaceToWorkYear, name },
    pageType,
  } = dataCompany

  const companyDataTrack = { id, name }

  const faqQuestions = getQuestions(dataCompany, t).filter(question => question.answer.length > 0)

  const isPremiumCompany = pageType === COMPANY_PAGE_TYPE.PREMIUM
  const hasFaqWithAnswers = faqQuestions.length > 0
  const isLastYear = new Date().getFullYear() - 1

  const displayFaq = faq && hasFaqWithAnswers
  const displayInsights =
    dataCurrentUser?.isLogged &&
    !!dataCompanyInsights?.experiencesInfos?.totalExperiences &&
    dataCompanyInsights?.experiencesInfos?.totalExperiences > 0 &&
    !!dataCompanyInsights?.experiencesInfos.experienceToDisplay?.insights.positiveFeedback
  const displayGreatPlaceToWork = !!greatPlaceToWorkPermalink && (greatPlaceToWorkYear || 0) >= isLastYear
  const displayCertification =
    isPremiumCompany && (displayGreatPlaceToWork || !!chooseMyCompany || !!glassdoorProfileUrl)
  const displayRecruitmentPatternBanner = isPremiumCompany && hasEnoughData

  const displayWorkThere = displayFaq || displayInsights || displayCertification || displayRecruitmentPatternBanner

  if (!displayWorkThere) return null

  return (
    <Layout colorBlock={LAYOUT_COLOR_BLOCK.WHITE} data-testid="company_information_work_there" hasBottomPadding>
      <Layout.Column>
        <Heading
          as="h2"
          className={styles.title}
          data-testid="company_information_work_there_title"
          variant="title2"
          weight="bold"
        >
          {t("companies_client.information.how.title", { name })}
        </Heading>
        <div className={styles.container}>
          {displayFaq && (
            <div className={styles.content}>
              <FaqDynamic className={styles.faq} companyDataTrack={companyDataTrack} questions={faqQuestions} />
            </div>
          )}
          {(displayInsights || displayCertification) && (
            <div className={styles.content}>
              {displayInsights && (
                <InsightsDynamic
                  careerCenterName={dataCareerCenter?.name}
                  className={styles.insights}
                  companyName={name}
                  experiences={dataCompanyInsights?.experiencesInfos}
                  isCareerCenterIntranet={dataCareerCenter?.intranet}
                />
              )}
              {displayCertification && (
                <CertificationDynamic
                  className={styles.certifications}
                  glassdoorUrl={glassdoorProfileUrl}
                  isChooseMyCompany={!!chooseMyCompany}
                  isGreatPlaceToWork={displayGreatPlaceToWork}
                />
              )}
            </div>
          )}
          {displayRecruitmentPatternBanner && (
            <div className={styles.content}>
              <RecruitmentPatternBannerDynamic
                className={styles.recruitmentBanner}
                companyDataTrack={companyDataTrack}
                href={`/${locale}/${COMPANY_BASENAME}/${companySlug}/${COMPANY_JOB_OFFERS}#${RECRUITMENT_PATTERN_ID}`}
                linkComponent={Link}
              />
            </div>
          )}
        </div>
      </Layout.Column>
    </Layout>
  )
}
